import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  PlusCircleIcon,
  LinkIcon,
  DocumentTextIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import toast from 'react-hot-toast'

import { userLibraryAPI, type LibraryItem } from '@/api/userLibrary'
import { logError } from '@utils/logger'

const LibraryPage: React.FC = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState<LibraryItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [yearFrom, setYearFrom] = useState<string>('')
  const [yearTo, setYearTo] = useState<string>('')
  const [tagQuery, setTagQuery] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  type AddMode = 'form' | 'bibtex' | 'pdf'
  const [addMode, setAddMode] = useState<AddMode>('form')
  const [bibtexInput, setBibtexInput] = useState('')
  const [pdfFile, setPdfFile] = useState<File | null>(null)

  const [newItem, setNewItem] = useState({
    title: '',
    authors: '',
    year: '',
    venue: '',
    doi: '',
    arxiv_id: '',
    url: '',
    tags: '',
    notes: '',
  })

  const hasFilters = useMemo(
    () => !!(search.trim() || yearFrom || yearTo || tagQuery.trim()),
    [search, yearFrom, yearTo, tagQuery],
  )

  const loadItems = async () => {
    setIsLoading(true)
    try {
      const params: any = {
        limit: 100,
        offset: 0,
      }
      if (search.trim()) params.search = search.trim()
      if (tagQuery.trim()) params.tags = tagQuery.trim()
      if (yearFrom) params.year_from = parseInt(yearFrom, 10)
      if (yearTo) params.year_to = parseInt(yearTo, 10)

      const data = await userLibraryAPI.listLibrary(params)
      setItems(data || [])
    } catch (error) {
      logError('Failed to load user library', error)
      toast.error('Failed to load your library')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleApplyFilters = async () => {
    await loadItems()
  }

  const handleResetFilters = async () => {
    setSearch('')
    setYearFrom('')
    setYearTo('')
    setTagQuery('')
    await loadItems()
  }

  const handleCreateManual = async (e: React.FormEvent) => {
    e.preventDefault()

    // PDF mode: minimal metadata, enforce size limit
    if (addMode === 'pdf') {
      if (!pdfFile) {
        toast.error('Please select a PDF file')
        return
      }
      const maxSizeBytes = 10 * 1024 * 1024 // 10MB
      if (pdfFile.size > maxSizeBytes) {
        toast.error('PDF is too large (max 10MB)')
        return
      }

      setIsSaving(true)
      try {
        // Basic extraction from filename (without extension)
        const nameWithoutExt = pdfFile.name.replace(/\.[^/.]+$/, '')

        const tags =
          newItem.tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean) || []

        await userLibraryAPI.addManualItem({
          title: nameWithoutExt || 'Untitled paper',
          authors: [],
          tags,
          notes: newItem.notes || undefined,
        })

        toast.success('PDF paper added to your library')
        setShowAddForm(false)
        setPdfFile(null)
        setBibtexInput('')
        setAddMode('form')
        setNewItem({
          title: '',
          authors: '',
          year: '',
          venue: '',
          doi: '',
          arxiv_id: '',
          url: '',
          tags: '',
          notes: '',
        })
        await loadItems()
      } catch (error) {
        logError('Failed to add PDF library item', error)
        toast.error('Failed to add paper from PDF')
      } finally {
        setIsSaving(false)
      }
      return
    }

    if (!newItem.title.trim()) {
      toast.error('Title is required')
      return
    }
    setIsSaving(true)
    try {
      const authors =
        newItem.authors
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean) || []
      const tags =
        newItem.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean) || []

      await userLibraryAPI.addManualItem({
        title: newItem.title.trim(),
        authors,
        year: newItem.year ? parseInt(newItem.year, 10) : undefined,
        venue: newItem.venue || undefined,
        doi: newItem.doi || undefined,
        arxiv_id: newItem.arxiv_id || undefined,
        url: newItem.url || undefined,
        tags,
        notes: newItem.notes || undefined,
      })

      toast.success('Paper added to your library')
      setShowAddForm(false)
      setNewItem({
        title: '',
        authors: '',
        year: '',
        venue: '',
        doi: '',
        arxiv_id: '',
        url: '',
        tags: '',
        notes: '',
      })
      await loadItems()
    } catch (error) {
      logError('Failed to add manual library item', error)
      toast.error('Failed to add paper')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 shadow-lg">
              <BookOpenIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
                My Library
              </h1>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                All papers you have saved across workspaces, citations, and manual uploads.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/discover')}
              className="hidden md:inline-flex items-center justify-center px-3 py-2 text-sm rounded-xl border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 bg-white/70 dark:bg-slate-900/40 hover:bg-white dark:hover:bg-slate-800 transition"
            >
              Back to Discover
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-medium shadow-md hover:shadow-lg transition"
            >
              <PlusCircleIcon className="w-4 h-4" />
              <span>Add paper</span>
            </button>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="mb-6"
        >
          <div className="bg-white/80 dark:bg-slate-900/60 backdrop-blur rounded-2xl border border-slate-200/80 dark:border-slate-700/80 p-4 md:p-5 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Search
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by title, author, or abstract..."
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-24">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Year from
                  </label>
                  <input
                    type="number"
                    value={yearFrom}
                    onChange={(e) => setYearFrom(e.target.value)}
                    className="w-full px-2 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                  />
                </div>
                <div className="w-24">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Year to
                  </label>
                  <input
                    type="number"
                    value={yearTo}
                    onChange={(e) => setYearTo(e.target.value)}
                    className="w-full px-2 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                  />
                </div>
                <div className="w-40">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Tags
                  </label>
                  <input
                    type="text"
                    value={tagQuery}
                    onChange={(e) => setTagQuery(e.target.value)}
                    placeholder="e.g. RCT, review"
                    className="w-full px-2 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  disabled={isLoading}
                  className={clsx(
                    'inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition',
                    'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-sm hover:shadow-md',
                    isLoading && 'opacity-70 cursor-not-allowed',
                  )}
                >
                  <MagnifyingGlassIcon className="w-4 h-4" />
                  <span>Apply</span>
                </button>
                <button
                  type="button"
                  onClick={handleResetFilters}
                  disabled={isLoading || !hasFilters}
                  className={clsx(
                    'inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium border transition',
                    'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 bg-white/70 dark:bg-slate-900/50 hover:bg-white dark:hover:bg-slate-800',
                    (!hasFilters || isLoading) && 'opacity-60 cursor-not-allowed',
                  )}
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  <span>Reset</span>
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <div className="space-y-4">
          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400 text-sm">
                <span className="inline-block h-5 w-5 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
                <span>Loading your library…</span>
              </div>
            </div>
          )}

          {!isLoading && items.length === 0 && (
            <div className="py-10 flex flex-col items-center justify-center text-center">
              <p className="text-slate-600 dark:text-slate-400 mb-2">
                You don&apos;t have any saved papers yet.
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-500 mb-4">
                Papers you save as evidence in workspaces will appear here automatically.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/discover')}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-medium shadow-md hover:shadow-lg transition"
                >
                  <MagnifyingGlassIcon className="w-4 h-4" />
                  <span>Start a workspace</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-600 bg-white/70 dark:bg-slate-900/50 text-sm text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 transition"
                >
                  <PlusCircleIcon className="w-4 h-4" />
                  <span>Add paper manually</span>
                </button>
              </div>
            </div>
          )}

          {!isLoading && items.length > 0 && (
            <div className="space-y-3">
              {items.map((item) => (
                <motion.div
                  key={item.paper_ref}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-900/70 backdrop-blur-sm px-4 py-3 md:px-5 md:py-4 hover:border-teal-400/80 hover:shadow-md transition"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-sm md:text-base text-slate-900 dark:text-white line-clamp-2 md:line-clamp-1">
                          {item.title || 'Untitled paper'}
                        </p>
                        {item.year && (
                          <span className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-700 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400">
                            {item.year}
                          </span>
                        )}
                      </div>
                      {item.authors && item.authors.length > 0 && (
                        <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-1">
                          {item.authors.join(', ')}
                        </p>
                      )}
                      {item.venue && (
                        <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                          {item.venue}
                        </p>
                      )}
                      {item.tags && item.tags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {item.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center rounded-full bg-teal-50 dark:bg-teal-900/30 text-[10px] font-medium text-teal-700 dark:text-teal-300 px-2 py-0.5"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-row md:flex-col gap-2 items-end md:items-end">
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-slate-900/90 dark:bg-slate-100 text-slate-50 dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200 transition"
                        >
                          <LinkIcon className="w-3.5 h-3.5" />
                          <span>Open</span>
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate('/discover')}
                        className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 bg-white/70 dark:bg-slate-900/40 hover:bg-white dark:hover:bg-slate-800 transition"
                      >
                        <BookOpenIcon className="w-3.5 h-3.5" />
                        <span>Use in workspace</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Add manual paper drawer */}
        {showAddForm && (
          <div className="fixed inset-0 z-40 flex items-end md:items-center justify-center bg-slate-900/40 backdrop-blur-sm">
            <div className="w-full md:max-w-xl bg-white dark:bg-slate-900 rounded-t-3xl md:rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Add paper to library
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Add a paper manually, paste a BibTeX entry, or attach a PDF (max 10MB).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => !isSaving && setShowAddForm(false)}
                  className="text-xs text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
                  disabled={isSaving}
                >
                  Close
                </button>
              </div>

              {/* Mode selector */}
              <div className="mb-3 flex items-center gap-2 text-xs">
                <span className="text-slate-500 dark:text-slate-400">Mode:</span>
                <div className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 p-1">
                  <button
                    type="button"
                    onClick={() => setAddMode('form')}
                    className={clsx(
                      'px-3 py-1 rounded-full flex items-center gap-1',
                      addMode === 'form'
                        ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 dark:text-slate-400',
                    )}
                  >
                    <DocumentTextIcon className="w-3.5 h-3.5" />
                    <span>Form</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddMode('bibtex')}
                    className={clsx(
                      'px-3 py-1 rounded-full flex items-center gap-1',
                      addMode === 'bibtex'
                        ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 dark:text-slate-400',
                    )}
                  >
                    <DocumentTextIcon className="w-3.5 h-3.5" />
                    <span>BibTeX</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddMode('pdf')}
                    className={clsx(
                      'px-3 py-1 rounded-full flex items-center gap-1',
                      addMode === 'pdf'
                        ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 dark:text-slate-400',
                    )}
                  >
                    <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                    <span>PDF</span>
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateManual} className="space-y-3 text-sm">
                {addMode === 'bibtex' && (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                      BibTeX entry
                    </label>
                    <textarea
                      rows={6}
                      value={bibtexInput}
                      onChange={(e) => setBibtexInput(e.target.value)}
                      placeholder="@article{key,\n  title={...},\n  author={...},\n  year={...},\n  journal={...}\n}"
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!bibtexInput.trim()) {
                          toast.error('Paste a BibTeX entry first')
                          return
                        }
                        try {
                          const text = bibtexInput
                          const getField = (name: string): string => {
                            const regex = new RegExp(
                              `${name}\\s*=\\s*[{"]([^"}]+)[}"]`,
                              'i',
                            )
                            const match = text.match(regex)
                            return match ? match[1].trim() : ''
                          }
                          const title = getField('title')
                          const authorField = getField('author')
                          const yearStr = getField('year')
                          const journal = getField('journal') || getField('booktitle')
                          const doi = getField('doi')
                          const url = getField('url')

                          const authors =
                            authorField
                              .split(/ and /i)
                              .map((a) => a.replace(/[{}]/g, '').trim())
                              .filter(Boolean) || []

                          setNewItem((prev) => ({
                            ...prev,
                            title: title || prev.title,
                            authors: authors.join(', '),
                            year: yearStr || prev.year,
                            venue: journal || prev.venue,
                            doi: doi || prev.doi,
                            url: url || prev.url,
                          }))

                          toast.success('Extracted fields from BibTeX')
                          setAddMode('form')
                        } catch (err) {
                          logError('Failed to parse BibTeX', err)
                          toast.error('Could not extract fields from BibTeX')
                        }
                      }}
                      className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200 transition"
                    >
                      <DocumentTextIcon className="w-3.5 h-3.5" />
                      <span>Extract into form</span>
                    </button>
                  </div>
                )}

                {addMode === 'pdf' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        PDF file<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null
                          setPdfFile(file)
                        }}
                        className="w-full text-xs text-slate-700 dark:text-slate-300"
                      />
                      <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-500">
                        Maximum file size 10MB. We&apos;ll extract basic metadata from the file name.
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Tags (optional)
                      </label>
                      <input
                        type="text"
                        value={newItem.tags}
                        onChange={(e) => setNewItem((prev) => ({ ...prev, tags: e.target.value }))}
                        placeholder="Comma-separated, e.g. RCT, systematic review"
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Notes (optional)
                      </label>
                      <textarea
                        rows={3}
                        value={newItem.notes}
                        onChange={(e) => setNewItem((prev) => ({ ...prev, notes: e.target.value }))}
                        placeholder="Optional notes about why this paper is important."
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>
                )}

                {addMode === 'form' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Title<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newItem.title}
                        onChange={(e) => setNewItem((prev) => ({ ...prev, title: e.target.value }))}
                        required
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Authors
                      </label>
                      <input
                        type="text"
                        placeholder="Comma-separated, e.g. Jane Doe, John Smith"
                        value={newItem.authors}
                        onChange={(e) => setNewItem((prev) => ({ ...prev, authors: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                          Year
                        </label>
                        <input
                          type="number"
                          value={newItem.year}
                          onChange={(e) => setNewItem((prev) => ({ ...prev, year: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                          Venue / Journal
                        </label>
                        <input
                          type="text"
                          value={newItem.venue}
                          onChange={(e) => setNewItem((prev) => ({ ...prev, venue: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                          DOI
                        </label>
                        <input
                          type="text"
                          value={newItem.doi}
                          onChange={(e) => setNewItem((prev) => ({ ...prev, doi: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                          arXiv ID
                        </label>
                        <input
                          type="text"
                          value={newItem.arxiv_id}
                          onChange={(e) => setNewItem((prev) => ({ ...prev, arxiv_id: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        URL
                      </label>
                      <input
                        type="url"
                        value={newItem.url}
                        onChange={(e) => setNewItem((prev) => ({ ...prev, url: e.target.value }))}
                        placeholder="Link to the paper (optional)"
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Tags
                      </label>
                      <input
                        type="text"
                        value={newItem.tags}
                        onChange={(e) => setNewItem((prev) => ({ ...prev, tags: e.target.value }))}
                        placeholder="Comma-separated, e.g. RCT, systematic review"
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Notes
                      </label>
                      <textarea
                        rows={3}
                        value={newItem.notes}
                        onChange={(e) => setNewItem((prev) => ({ ...prev, notes: e.target.value }))}
                        placeholder="Optional notes about why this paper is important."
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/70 focus:border-transparent resize-none"
                      />
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between pt-2">
                  <p className="text-[11px] text-slate-500 dark:text-slate-500">
                    You can always edit or extend metadata later from the backend.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => !isSaving && setShowAddForm(false)}
                      disabled={isSaving}
                      className="px-3 py-2 text-xs rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 bg-white/70 dark:bg-slate-900/40 hover:bg-white dark:hover:bg-slate-800 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving}
                      className={clsx(
                        'inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium text-white bg-gradient-to-r from-teal-500 to-cyan-500 shadow-sm hover:shadow-md transition',
                        isSaving && 'opacity-70 cursor-not-allowed',
                      )}
                    >
                      {isSaving && (
                        <span className="inline-block h-3 w-3 rounded-full border-2 border-white/80 border-t-transparent animate-spin" />
                      )}
                      <span>{isSaving ? 'Saving…' : 'Add to library'}</span>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LibraryPage


